// src/pages/Login/Login.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import { getLandingPath } from "@/app/router-helpers";
import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";
import { PATHS } from "@/app/constants";

import cls from "./Login.module.scss";

import LogoS02 from "@/assets/NEW/logo/S02.webp";

import MrM from "@/assets/UI/home/MrM.webp";
import MrsM from "@/assets/UI/home/MrsM.webp";
import MrD from "@/assets/UI/home/MrD.webp";
import MrsD from "@/assets/UI/home/MrsD.webp";

const bgFloatA = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.5, 0.68, 0.5],
    scale: [1.03, 1.08, 1.03],
    x: [0, 8, 0],
    transition: { duration: 13, ease: "easeInOut", repeat: Infinity },
  },
};

const bgFloatB = {
  hidden: { opacity: 0.78 },
  show: {
    opacity: [0.78, 0.62, 0.78],
    scale: [1.08, 1.03, 1.08],
    x: [0, -10, 0],
    transition: { duration: 15, ease: "easeInOut", repeat: Infinity },
  },
};

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const t = (key, options) => i18n.t(key, options);

  const [identifier, setIdentifier] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  const ENABLE_RESET = true;

  const lang = (i18n.language || "en").slice(0, 2);

  const forgotPassword = async () => {
    if (!ENABLE_RESET) {
      toast.info(
        t("common.soon", {
          defaultValue: "Password reset is coming soon.",
        }),
      );
      return;
    }

    const email = String(identifier || "")
      .trim()
      .toLowerCase();

    if (!email || !email.includes("@")) {
      toast.info(
        t("login.enterEmailForReset", {
          defaultValue: "Please enter your email first.",
        }),
      );
      return;
    }

    try {
      await api.post(
        "/v1/auth/password/forgot",
        { email },
        { skipToast: true },
      );

      toast.success(
        t("login.resetSent", {
          defaultValue: "Password reset link sent.",
        }),
      );
    } catch {
      toast.error(
        t("login.resetFailed", {
          defaultValue: "Could not send reset link. Please try again.",
        }),
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");

    const fd = new FormData(e.currentTarget);
    const nextIdentifier = String(fd.get("identifier") || "").trim();
    const password = String(fd.get("password") || "");

    setIdentifier(nextIdentifier);

    if (!nextIdentifier || !password) {
      setErrorText(
        t("login.errors.identifierRequired", {
          defaultValue: "Please enter your email/username and password.",
        }),
      );
      return;
    }

    try {
      setSubmitting(true);

      const { data } = await api.post("/v1/auth/login", {
        identifier: nextIdentifier,
        password,
        lang,
      });

      if (data?.accessToken) {
        localStorage.setItem("cm_at", data.accessToken);
      }

      const emailVerifiedValue =
        data?.user?.emailVerifiedAt ??
        data?.user?.email_verified ??
        data?.user?.emailVerified ??
        null;

      const user = {
        status: "active",
        ...(data?.user || {}),
        ...(emailVerifiedValue != null
          ? { email_verified: !!emailVerifiedValue }
          : {}),
      };

      login(user, data?.accessToken);

      toast.success(
        t("login.toasts.welcomeBack", {
          defaultValue: "Welcome back.",
        }),
      );

      navigate(getLandingPath(user), { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const payload = err?.response?.data || {};
      const serverMessage = payload?.message || "";
      const code = payload?.code;

      if (
        status === 403 &&
        (code === "EMAIL_NOT_VERIFIED" || /verify/i.test(serverMessage))
      ) {
        const VERIFY_URL = (PATHS && PATHS.VERIFY_EMAIL) || "/verify-email";

        const redirect =
          payload?.redirect ||
          (nextIdentifier.includes("@")
            ? `${VERIFY_URL}?email=${encodeURIComponent(nextIdentifier)}`
            : VERIFY_URL);

        setErrorText(
          t("login.verifyNeeded", {
            defaultValue: "Please verify your email before logging in.",
          }),
        );

        if (/^https?:\/\//i.test(redirect)) {
          try {
            const url = new URL(redirect);
            navigate(`${VERIFY_URL}${url.search || ""}`, { replace: true });
          } catch {
            window.location.href = redirect;
          }
        } else {
          navigate(redirect, { replace: true });
        }

        return;
      }

      if (status === 429) {
        const tooManyMessage = t("login.toasts.tooMany", {
          defaultValue: "Too many login attempts. Please try again later.",
        });

        setErrorText(tooManyMessage);
        toast.error(tooManyMessage);
        return;
      }

      const message =
        serverMessage ||
        (status === 401
          ? t("login.errors.invalid", {
              defaultValue: "Invalid login details.",
            })
          : t("login.errors.fail", {
              defaultValue: "Login failed. Please try again.",
            }));

      setErrorText(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={cls.page}>
      <section
        className={cls.heroStage}
        aria-label={t("login.ariaLabel", "Login to Noty Universe")}
      >
        <div className={cls.universeFx} aria-hidden="true">
          <span className={cls.orbitOne} />
          <span className={cls.orbitTwo} />
          <span className={cls.energyLine} />
          <span className={cls.particles} />
        </div>

        <div className={cls.mobileOnly} aria-hidden="true">
          <Motion.img
            className={`${cls.heroImg} ${cls.z0}`}
            src={MrM}
            alt=""
            variants={bgFloatA}
            initial="hidden"
            animate="show"
            draggable="false"
          />

          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrsM}
            alt=""
            variants={bgFloatB}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly} aria-hidden="true">
          <Motion.img
            className={`${cls.heroImg} ${cls.z0}`}
            src={MrD}
            alt=""
            variants={bgFloatA}
            initial="hidden"
            animate="show"
            draggable="false"
          />

          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrsD}
            alt=""
            variants={bgFloatB}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.darkVeil} aria-hidden="true" />

        <div className={cls.overlay}>
          <Motion.div
            className={cls.logoStack}
            initial={{ opacity: 0, y: -12, scale: 0.88 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className={cls.logoPortal}>
              <span className={cls.logoGlow} />
              <span className={cls.logoRing} />
              <img src={LogoS02} alt="Noty Universe" className={cls.logo} />
            </div>

            <div
              className={cls.districtMorph}
              aria-label="Noty Universe districts"
            >
              <div className={cls.districtNames}>
                <span>
                  <strong>N</strong>
                  <em>ova</em>
                </span>

                <span>
                  <strong>O</strong>
                  <em>racle</em>
                </span>

                <span>
                  <strong>T</strong>
                  <em>erra</em>
                </span>

                <span>
                  <strong>Y</strong>
                  <em>ara</em>
                </span>
              </div>

              <div className={cls.notyWord}>
                <span>NOTY</span>
                <small>UNIVERSE</small>
              </div>
            </div>
          </Motion.div>

          <Motion.div
            className={cls.loginCard}
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.12 }}
          >
            <div className={cls.introBox}>
              <h1>{t("login.title", "Welcome Back")}</h1>
              <p>
                {t(
                  "login.subtitle",
                  "Enter the universe and continue your journey through connection, ideas, and free thinking.",
                )}
              </p>
              <small>
                {t(
                  "login.small",
                  t("login.slogan", "Noty Universe Citizen Access"),
                )}
              </small>
            </div>

            <form className={cls.form} noValidate onSubmit={handleSubmit}>
              {errorText ? (
                <div className={cls.errorText} role="alert">
                  {errorText}
                </div>
              ) : null}

              <div className={cls.group}>
                <label className={cls.label} htmlFor="identifier">
                  {t("login.identifierLabel", "Email or Username")}
                </label>

                <input
                  id="identifier"
                  name="identifier"
                  className={cls.input}
                  type="text"
                  inputMode="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t(
                    "login.identifierPlaceholder",
                    "Enter your email or username",
                  )}
                  autoComplete="username"
                  required
                />

                <div className={cls.help}>
                  {t(
                    "login.identifierHelp",
                    "Use your registered email or username.",
                  )}
                </div>
              </div>

              <div className={cls.group}>
                <label className={cls.label} htmlFor="password">
                  {t("login.passwordLabel", "Password")}
                </label>

                <div className={cls.passwordRow}>
                  <input
                    id="password"
                    name="password"
                    className={cls.input}
                    type={showPassword ? "text" : "password"}
                    placeholder={t(
                      "login.passwordPlaceholder",
                      "Enter your password",
                    )}
                    autoComplete="current-password"
                    required
                  />

                  <button
                    className={cls.eyeBtn}
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={t(
                      "login.showHide",
                      showPassword ? "Hide password" : "Show password",
                    )}
                    title={t(
                      "login.showHide",
                      showPassword ? "Hide password" : "Show password",
                    )}
                    aria-pressed={showPassword ? "true" : "false"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>

                <span className={cls.help}>
                  {t(
                    "login.help",
                    "Use the same login details you used during registration.",
                  )}
                </span>
              </div>

              <button
                className={cls.submit}
                type="submit"
                disabled={submitting}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("login.signingIn", "Entering...")
                  : t("login.submit", t("login.loginBtn", "Enter Universe"))}
              </button>

              <div className={cls.actions}>
                <button
                  className={cls.forgotBtn}
                  type="button"
                  onClick={forgotPassword}
                >
                  {t(
                    "login.forgotPassword",
                    t("login.forgot", "Forgot password?"),
                  )}
                </button>
              </div>

              <div className={cls.altRow}>
                <span>{t("login.noAccount", "New to Noty Universe?")}</span>

                <Link className={cls.link} to="/register">
                  {t(
                    "login.createAccount",
                    t("login.createOne", "Create your citizenship"),
                  )}
                </Link>
              </div>
            </form>
          </Motion.div>
        </div>
      </section>
    </main>
  );
}
